import {Box} from "@mui/material";
import {
    Eventcalendar,
    localeDe,
    localeEn,
    MbscCalendarColor,
    MbscCalendarEvent,
    MbscCellClickEvent,
    MbscEventClickEvent,
    MbscEventCreatedEvent,
    MbscEventUpdatedEvent,
    MbscPageChangeEvent,
    MbscResource
} from "@mobiscroll/react";
import {print} from '@mobiscroll/print';
import ReservationEventPreview from "../ReservationEventPreview";
import {
    BLOCK_OVERLAP_SECONDS,
    checkBlockOverlapping,
    slotContainsExistingResource,
    UnassignedType
} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {ReservationActions, useReservationsState} from "../../../store/reservations.reducer";
import {endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {getCalendarTimezoneProps, toJsDate, withJsDates} from "crqlar-react-common/dist/utils/calendarTimezone";
import {persistSectorDate, readSectorDate} from "../sectorDate";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Reservation
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {useDispatch} from "react-redux";
import {
    EventcalendarBase
} from "@mobiscroll/react/dist/src/core/components/eventcalendar/eventcalendar";
import {
    AgeCategory
} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import ServiceEventPreview from "../services/ServiceEventPreview";
import {
    Availability
} from "crqlar-react-common/dist/models/availability/availability.model";
import {
    AvailabilityCalculationResult
} from "crqlar-react-common/dist/models/availability/availabilityCalculationResult.model";
import CalendarEventReservationsTooltip from "./CalendarEventReservationsTooltip";
import CalendarEventServicesTooltip from "../services/CalendarEventServicesTooltip";
import {
    AvailabilityException
} from "crqlar-react-common/dist/models/availability/availabilityException.model";
import {useResourceOccupancy} from "./util/useResourceOccupancy";
import {useCurrentAppId} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useCalendarView} from "./util/useCalendarView";
import {useMbscEventConnections} from "./util/useMbscEventConnections";
import {timelineRowHeight, withTimelineEventLanes} from "./util/timelineEventLanes";
import {QueryObserverResult} from "react-query";
import CommonConfirmDialog from "crqlar-react-common/dist/components/dialog/CommonConfirmDialog";

const TOUCH_DOUBLE_TAP_DELAY_MS = 350
const TOOLTIP_CLOSE_DELAY_MS = 200

interface Props {
    timelineSpan: 'day' | 'week' | 'month' | undefined
    viewMode: 'timeline' | 'timeGrid' | undefined
    events: MbscCalendarEvent[]
    selectedEventFromSearch: MbscCalendarEvent | undefined | null
    mbscResources: MbscResource[] | undefined | null
    setMarkedReservation: (reservation: Reservation | undefined) => void
    setMarkedService: (service: Service | undefined, availability: Availability | undefined, reservations: Reservation[] | undefined, startTime: Date, endTime: Date, hasException: boolean) => void
    handleDeselect: () => void
    renderMyResource: (mbscResource: MbscResource) => JSX.Element
    mbscResourceColumnType: "resources" | "services" | undefined
    setShowAlert: (show: boolean) => void
    updateReservationViaCalendarAction: (
        calendarActionArgs: MbscEventUpdatedEvent
    ) => Promise<
        {
            originalReservation: undefined;
            updatedReservation: undefined;
        } | {
        originalReservation: Reservation;
        updatedReservation: Reservation;
    }
    >
    setTmpUpdatedReservation: (reservation: Reservation | undefined) => void
    setTmpOriginalReservation: (reservation: Reservation | undefined) => void
    createNewReservation: (event: MbscEventCreatedEvent) => void
    fromDate: Date | undefined
    toDate: Date | undefined
    startDateFinal: Date | undefined
    calendarRef: React.MutableRefObject<Eventcalendar | null>

    sector: Sector | undefined
    reservations: Reservation[]
    services: Service[]
    serviceReservations: Reservation[]
    profiles: Profile[]
    availabilityColors: MbscCalendarColor[],
    // Visual-only superset of availabilityColors (e.g. parent block mirrored onto child tables)
    availabilityDisplayColors?: MbscCalendarColor[],
    ageCategories: AgeCategory[]
    disableDragEvents?: boolean
    refreshResList?: () => Promise<QueryObserverResult<Reservation[], unknown>>
    updateReservationInCache: (updatedReservation: Reservation) => void
    blockPeriodEvents: MbscCalendarEvent[]
    onBlockPeriodClick?: (calcResult: AvailabilityCalculationResult) => void
    onBlockPeriodUpdated?: (availabilityId: string, newStart: Date, newEnd: Date, newResourceId?: string) => Promise<void>
}

const CalendarComponent = (props: Props) => {
    const {t, i18n} = useTranslation()

    // A read-only user can browse the calendar and open events, but cannot create, move
    // or resize reservations by drag/click. Combined with the existing disableDragEvents prop.
    const readOnly = useReadOnly()
    const disableEditing = props.disableDragEvents || readOnly

    const calendarLocale = {
        ...(i18n.language === 'de' ? localeDe : localeEn)
    }

    const myModules = [print]

    const appId = useCurrentAppId()
    const {
        // We need timelineStep for occupancy calculation
        timelineStep,
    } = useReservationsState(appId)

    const dispatch = useDispatch()
    const pageChangeTimer = useRef<ReturnType<typeof setTimeout>>()

    const {
        // Used for force rerendering the calendar on navigation actions
        calendarNavigationKey
    } = useReservationsState(appId)

    // Use calview hook
    const calView = useCalendarView(appId, props.reservations, props.startDateFinal as Date)

    const currentPageStart = props.fromDate ? startOfDay(props.fromDate).getTime() : undefined
    const currentPageEnd = props.toDate ? endOfDay(props.toDate).getTime() : undefined

    const handlePageChange = useCallback((event: MbscPageChangeEvent) => {
        const nextFromDate = startOfDay(toJsDate(event.firstDay))
        const nextToDate = endOfDay(toJsDate(event.lastDay))

        // Mobiscroll can emit a page change while rendering a controlled date update.
        // Ignore the range it already has and let the current render finish before dispatching.
        if (nextFromDate.getTime() === currentPageStart && nextToDate.getTime() === currentPageEnd) {
            return
        }

        if (pageChangeTimer.current) {
            clearTimeout(pageChangeTimer.current)
        }
        pageChangeTimer.current = setTimeout(() => {
            dispatch({
                type: ReservationActions.SET_RESERVATIONS,
                appId: appId,
                payload: {
                    fromDate: nextFromDate.toISOString(),
                    toDate: nextToDate.toISOString()
                }
            })
            // Navigating inside Mobiscroll (swipe, keyboard) bypasses the header picker,
            // so mirror the new day into the sector-wide key the floor plan and the day
            // lists read. Only on a day page, where the page start IS the selected day —
            // on a week/month page it is merely the first cell. The equality check skips
            // the page change Mobiscroll emits for the range it was mounted with.
            const storedDay = readSectorDate(props.sector?.id)?.date
            if (
                props.timelineSpan === 'day'
                && storedDay?.getTime() !== nextFromDate.getTime()
            ) {
                persistSectorDate(props.sector?.id, {date: nextFromDate, mode: 'fixed'})
            }
            pageChangeTimer.current = undefined
        }, 0)
    }, [appId, currentPageEnd, currentPageStart, dispatch, props.sector?.id, props.timelineSpan])

    useEffect(() => () => {
        if (pageChangeTimer.current) {
            clearTimeout(pageChangeTimer.current)
        }
    }, [])

    const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement>()
    const [hoveredEvent, setHoveredEvent] = useState<MbscEventClickEvent | undefined>(undefined)

    const timer = useRef<ReturnType<typeof setTimeout>>()
    const tooltipAnchorRef = useRef<HTMLElement>()
    const reservationTooltipContentRef = useRef<HTMLDivElement | null>(null)
    const lastPointerInteraction = useRef<{type: string, timestamp: number}>()
    const lastTouchTap = useRef<{eventKey: string, timestamp: number}>()

    const clearTooltipCloseTimer = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current)
            timer.current = undefined
        }
    }, [])

    const isPointerOverReservationTooltip = useCallback(() => {
        return !!(
            tooltipAnchorRef.current?.matches(':hover')
            || reservationTooltipContentRef.current?.matches(':hover')
        )
    }, [])

    const closeTooltip = useCallback(() => {
        clearTooltipCloseTimer()
        tooltipAnchorRef.current = undefined
        setHoveredEvent(undefined)
        setTooltipAnchor(undefined)
    }, [clearTooltipCloseTimer])

    const scheduleTooltipClose = useCallback(() => {
        clearTooltipCloseTimer()
        timer.current = setTimeout(() => {
            timer.current = undefined

            if (props.mbscResourceColumnType !== 'services' && isPointerOverReservationTooltip()) {
                return
            }

            closeTooltip()
        }, TOOLTIP_CLOSE_DELAY_MS)
    }, [clearTooltipCloseTimer, closeTooltip, isPointerOverReservationTooltip, props.mbscResourceColumnType])

    useEffect(() => clearTooltipCloseTimer, [clearTooltipCloseTimer])

    const handleEventUpdate = async (event: MbscEventUpdatedEvent) => {
        const {
            originalReservation,
            updatedReservation
        } = await props.updateReservationViaCalendarAction(event)
        // We still need to ensure all resources with virtual ids get assigned to undefined / null
        // When updating, the new resourceIds come from events, which contain previously assigned virtual ids
        if (updatedReservation?.resourceIds?.some(id => id?.startsWith(UnassignedType.UNASSIGNED_RESOURCE))) {
            updatedReservation.resourceIds = undefined
        }

        if (updatedReservation?.serviceId?.startsWith(UnassignedType.UNASSIGNED_SERVICE)) {
            updatedReservation.serviceId = undefined
        }

        updatedReservation && props.setTmpUpdatedReservation(updatedReservation)
        originalReservation && props.setTmpOriginalReservation(originalReservation)
    }

    // Creation on a child resource (table) inside its parent's (restaurant) block. Not blocked —
    // availability only lives on the parent — but the user is warned first.
    const [pendingBlockedChildCreate, setPendingBlockedChildCreate] = useState<MbscEventCreatedEvent>()

    const cancelBlockedChildCreate = () => {
        pendingBlockedChildCreate?.inst?.removeEvent(pendingBlockedChildCreate.event)
        setPendingBlockedChildCreate(undefined)
    }

    const confirmBlockedChildCreate = () => {
        if (pendingBlockedChildCreate) props.createNewReservation(pendingBlockedChildCreate)
        setPendingBlockedChildCreate(undefined)
    }

    // Calendar mobiscroll connections between events (for reservations spanning multiple resources)
    const connections = useMbscEventConnections({
        events: props.events,
        rangeStart: props.fromDate,
        rangeEnd: props.toDate,
        enabled: props.viewMode === 'timeline'
    })

    const showCalendarDragError = (
        calendar: EventcalendarBase | undefined,
        newEvent: MbscCalendarEvent | undefined,
        oldEvent: MbscCalendarEvent | undefined,
        title: string,
        tooltip: string
    ) => {
        calendar?.updateEvent({
            ...newEvent,
            title: title,
            tooltip: tooltip,
            cssClass: 'calendar-drag-error'
        })

        // Keep for 3 seconds the old event, so user can see what was the original slot
        setTimeout(() => {
            // RETURNING THE OLD EVENT BACK
            calendar?.updateEvent(oldEvent as MbscCalendarEvent)
        }, BLOCK_OVERLAP_SECONDS * 1000)
    }

    // Tooltip handlers

    const openTooltip = useCallback((args: MbscEventClickEvent) => {
        clearTooltipCloseTimer()

        let anchor: HTMLElement | null
        if (props.viewMode === 'timeGrid') {
            anchor = args.domEvent.target.closest('.mbsc-schedule-event')
        } else {
            anchor = args.domEvent.target.closest('.mbsc-timeline-event')
        }

        tooltipAnchorRef.current = anchor || undefined
        setTooltipAnchor(anchor || undefined)
        setHoveredEvent({...args, event: withJsDates(args.event)})
    }, [clearTooltipCloseTimer, props.viewMode])

    const handleEventHoverIn = useCallback(
        (args: MbscEventClickEvent) => {
            if (args.event?.isBlockPeriod) return
            openTooltip(args)
        },
        [openTooltip],
    )

    const handleEventHoverOut = useCallback(() => {
        scheduleTooltipClose()
    }, [scheduleTooltipClose])

    const handleMouseEnter = useCallback(() => {
        clearTooltipCloseTimer()
    }, [clearTooltipCloseTimer])

    const handleMouseLeave = useCallback(() => {
        scheduleTooltipClose()
    }, [scheduleTooltipClose])

    const handleCellClick = useCallback((args: MbscCellClickEvent) => {
        // In timeGrid view Mobiscroll fires onCellClick even when the user clicks directly
        // on an event element. Bail out early so onEventClick handles it exclusively and
        // the block-period logic cannot intercept a reservation/service click.
        if (props.viewMode === 'timeGrid') {
            const target = args.domEvent?.target as HTMLElement | null
            if (target?.closest('.mbsc-schedule-event')) return
        }

        lastTouchTap.current = undefined

        const clicked = toJsDate(args.date);
        const clickedResource = String(args.resource);
        const match = props.blockPeriodEvents.find(bp => {
            const start = new Date(bp.start as Date);
            const end = new Date(bp.end as Date);
            return (
                String(bp.resource) === clickedResource &&
                clicked >= start &&
                clicked < end
            );
        });
        if (match) {
            props.onBlockPeriodClick?.(match.blockCalcResult as AvailabilityCalculationResult);
        } else {
            props.handleDeselect();
        }
    }, [props.viewMode, props.blockPeriodEvents, props.onBlockPeriodClick, props.handleDeselect])

    // Opens the reservation or service dialogue from Mobiscroll event clicks and tooltip actions.
    const handleEventOpen = useCallback((eventData: any) => {
        const reservation = props.reservations.find(r => r.id === eventData?.reservationId)
        if (reservation?.id) {
            props.setMarkedReservation(reservation)
        }

        const service = props.services.find(s => s.id === eventData?.serviceId)
        if (service?.id) {
            const reservations = eventData?.reservations ?? [] as Reservation[]
            const exceptions = (eventData?.exceptions ?? []) as AvailabilityException[]
            const start = toJsDate(eventData.start)
            const end = toJsDate(eventData.end)

            const hasException = exceptions.some((exception: AvailabilityException) => {
                if (!exception.originalStartTime || !exception.newEndTime) return false;
                const exceptionStart = new Date(exception.originalStartTime);
                const exceptionEnd = new Date(exception.newEndTime);
                const eventStartTime = new Date(start).getTime();
                const eventEndTime = new Date(end).getTime();
                return eventStartTime < exceptionEnd.getTime() && eventEndTime > exceptionStart.getTime();
            });

            const availability = eventData.availability as Availability
            props.setMarkedService(service, availability, reservations, start, end, hasException)
        }
    }, [props.reservations, props.services, props.setMarkedReservation, props.setMarkedService])

    const handleEventClick = useCallback((event: MbscEventClickEvent) => {
        const pointerInteraction = lastPointerInteraction.current
        lastPointerInteraction.current = undefined

        if (event.event.isBlockPeriod) {
            lastTouchTap.current = undefined
            props.onBlockPeriodClick?.(event.event.blockCalcResult as AvailabilityCalculationResult)
            return
        }

        const now = Date.now()
        const isTouchInteraction = !!pointerInteraction
            && pointerInteraction.type !== 'mouse'
            && now - pointerInteraction.timestamp < 1000

        if (isTouchInteraction) {
            const eventKey = String(event.event.id ?? [
                event.event.reservationId,
                event.event.serviceId,
                event.event.start,
                event.event.resource,
            ].join('-'))
            const previousTap = lastTouchTap.current
            const isDoubleTap = previousTap?.eventKey === eventKey
                && now - previousTap.timestamp <= TOUCH_DOUBLE_TAP_DELAY_MS

            lastTouchTap.current = isDoubleTap ? undefined : {eventKey, timestamp: now}

            if (isDoubleTap) {
                closeTooltip()
                handleEventOpen(event.event)
            } else {
                openTooltip(event)
            }
            return
        }

        lastTouchTap.current = undefined
        closeTooltip()
        handleEventOpen(event.event)
    }, [closeTooltip, handleEventOpen, openTooltip, props.onBlockPeriodClick])

    const handleEventDragStart = useCallback(() => {
        lastPointerInteraction.current = undefined
        lastTouchTap.current = undefined
        closeTooltip()
    }, [closeTooltip])

    // Tag block-period events with a CSS class so the container can lower their z-index
    // below regular reservation/service events. This prevents block-period wrappers from
    // sitting above reservation wrappers in timeGrid stacking order and stealing hover/click.
    //
    // In the timeline the events also get a lane, so that reservations running at the same time
    // on one resource sit next to each other instead of on top of each other. timeGrid needs
    // none of this: Mobiscroll splits the column width there and nothing is hidden.
    const {data: calendarData, laneCount} = useMemo(() => {
            const tagged = props.events.map(e =>
                e.isBlockPeriod
                    ? { ...e, cssClass: [e.cssClass, 'block-period-event'].filter(Boolean).join(' ') }
                    : e
            )

            if (props.viewMode === 'timeGrid') {
                return {data: tagged, laneCount: 1}
            }

            const {events, laneCount} = withTimelineEventLanes(tagged)
            return {data: events, laneCount}
        },
        [props.events, props.viewMode]
    )

    // Occupancy calculation and rendering
    const { renderHourFooter, renderHour, totalActiveTableCount } = useResourceOccupancy({
        mbscResources: props.mbscResources,
        events: props.events,
        timelineStep: timelineStep || 30,
        sector: props.sector,
        // Only a single day timeline has one unambiguous day behind its hour columns,
        // which is what lets the hook place the columns of a DST switch day correctly
        renderedDay: props.timelineSpan === 'day' ? (props.startDateFinal ?? props.fromDate) : undefined
    })

    return (
        <Box
            className={`print-wrapper${props.timelineSpan === 'day' ? ' print-wrapper--single-day' : ''}`}
            onPointerDownCapture={(event) => {
                lastPointerInteraction.current = {
                    type: event.pointerType,
                    timestamp: Date.now()
                }
            }}
            sx={{
                width: '100%',
                height: '100%',
                // Timeline rows keep their usual height until reservations actually overlap;
                // from there they grow so every lane stays readable (see ReservationTimelineTab.css).
                '--calendar-timeline-row-height': `${timelineRowHeight(laneCount)}px`,
                // Keep block-period event wrappers below reservation/service event wrappers
                // in timeGrid stacking order so hover and click reach the correct event.
                '& .mbsc-schedule-event.block-period-event': {
                    zIndex: '0 !important',
                },
                // Same in the timeline (Mobiscroll puts every timeline event at z-index 2): the
                // restaurant block mirrored onto table rows spans the full row, so reservations on
                // those tables must stay on top of it to remain clickable and draggable.
                '& .mbsc-timeline-event.block-period-event': {
                    zIndex: '1 !important',
                },
            }}>
            <Eventcalendar
                key={
                    // In regular 'day' view -> key is static appId
                    // For week and month view -> we need to force rerendering the calendar
                    // Rerender only for week / month view, for daily it can be dangerous
                    props.timelineSpan === 'day'
                        ? appId
                        : calendarNavigationKey
                }
                theme="windows"
                themeVariant="light"
                renderHour={renderHour}
                renderHourFooter={renderHourFooter}
                locale={calendarLocale}
                view={calView}
                {...getCalendarTimezoneProps()}
                data={calendarData}
                selectedEvents={[props.selectedEventFromSearch] as MbscCalendarEvent[]}
                resources={props.mbscResources ?? undefined}
                onEventClick={handleEventClick}
                onEventDragStart={handleEventDragStart}
                onEventHoverIn={handleEventHoverIn}
                onEventHoverOut={handleEventHoverOut}
                // When clicking on empty areas - deselecting the event; also catches block period clicks in the dead zone
                onCellClick={handleCellClick}
                showControls={false}
                // The custom event previews own their hover titles. Mobiscroll's native
                // event tooltip covers the entire event wrapper, including empty space.
                showEventTooltip={false}
                renderResource={props.renderMyResource}
                renderScheduleEvent={(event) => {
                    // block period preview
                    if (event.original?.isBlockPeriod) {
                        return (
                            <Box sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'flex-start',
                                cursor: 'pointer',
                                boxSizing: 'border-box',
                                overflow: 'hidden',
                            }}>
                                {/*<Box*/}
                                {/*    component="span"*/}
                                {/*    sx={{*/}
                                {/*        fontSize: '0.65rem',*/}
                                {/*        fontWeight: 'bold',*/}
                                {/*        color: '#7b0000',*/}
                                {/*        overflow: 'hidden',*/}
                                {/*        textOverflow: 'ellipsis',*/}
                                {/*        whiteSpace: 'nowrap',*/}
                                {/*    }}*/}
                                {/*>*/}
                                {/*    {event.original.title || t('Block')}*/}
                                {/*</Box>*/}
                            </Box>
                        )
                    }

                    // service preview
                    if (event.original?.serviceId) {
                        return (
                            <div style={{ width: '100%', height: '100%' }}>
                                <ServiceEventPreview
                                    event={event}
                                    service={props.services.find(s => s.id === event.original?.serviceId)}
                                    ageCategories={props.ageCategories}
                                />
                            </div>
                        )
                    }

                    // reservation preview
                    return (
                        <div style={{ width: '100%', height: '100%' }}>
                            <ReservationEventPreview
                                reservation={props.reservations?.find(r => r.id === event.original?.reservationId)}
                                event={event}
                                profiles={props.profiles.filter(p => props.reservations
                                    ?.find(r => r.id === event.original?.reservationId)?.profileIds
                                    ?.includes(p.id!)
                                )}
                                highlightSearchResult={props.selectedEventFromSearch?.id === event.uid}
                                mbscResourceColumnType={props.mbscResourceColumnType}
                                ageCategories={props.ageCategories}
                                calendarView={props.viewMode ?? 'timeline'}
                            />
                        </div>
                    )
                }}
                extendDefaultEvent={(args) => {
                    return {
                        title: ('New reservation'),
                    }
                }}
                dragToMove={!disableEditing}
                dragToResize={!disableEditing}
                dragToCreate={!disableEditing}
                dragBetweenSlots={!disableEditing}
                dragBetweenResources={!disableEditing}
                clickToCreate={readOnly ? false : 'double'}
                onEventCreateFailed={() => {
                    props.setShowAlert(true)
                }}
                onEventUpdateFailed={() => {
                    props.setShowAlert(true)
                }}
                onEventUpdate={(event) => {
                    const reservation = props.reservations.find(r => r.id === event.event.reservationId)
                    // if (reservation) {
                    //     setMarkedReservation(reservation)
                    // }
                }}
                onEventUpdated={async (args) => {
                    // The calendar renders in the organization's timezone and hands dates back as
                    // ISO strings / timezone aware objects - continue with plain JS Dates
                    const event: MbscEventUpdatedEvent = {
                        ...args,
                        event: withJsDates(args.event),
                        oldEvent: withJsDates(args.oldEvent),
                    }
                    // Automatically remove the slot after 5 seconds
                    const calendar = event.inst
                    const newEvent = event.event
                    const oldEvent = event.oldEvent

                    // Handle block period resize/move
                    if (newEvent.isBlockPeriod) {
                        await props.onBlockPeriodUpdated?.(
                            newEvent.availabilityId as string,
                            toJsDate(newEvent.start),
                            toJsDate(newEvent.end),
                            // A block on a table row is the restaurant's block -> it stays on the restaurant
                            (newEvent.isInheritedBlockPeriod ? newEvent.parentResourceId : newEvent.resource) as string
                        )
                        return
                    }

                    /*
                        If someone drags the slot to the resource that is already included in the reservation
                        -> Show drag error and return to the previous slot position in calendar
                    */
                    if (slotContainsExistingResource(newEvent, oldEvent, props.reservations)) {
                        showCalendarDragError(
                            calendar,
                            newEvent,
                            oldEvent,
                            t('Resource already exists!'),
                            t('Cannot move a reservations to existing resource!')
                        )
                    }

                    /*
                        If someone drags the slot to the slot that is block
                        -> Show drag error and return to the previous slot position in calendar
                    */
                    else if (checkBlockOverlapping(event, props.availabilityColors)) {
                        showCalendarDragError(
                            calendar,
                            newEvent,
                            oldEvent,
                            t('Block overlap!'),
                            t('Cannot move a reservations to block slot!')
                        )
                    } else {
                        // Only if there are no dragging conflicts -> move the slot and update the reservation
                        handleEventUpdate(event)
                    }
                }}
                onEventCreated={(event) => {
                    if (checkBlockOverlapping(event, props.availabilityColors)) {
                        // Automatically remove the slot after 5 seconds
                        const calendar = event.inst
                        const createdEvent = event.event

                        calendar?.updateEvent({
                            ...createdEvent,
                            title: t('Block overlap!'),
                            tooltip: t('Cannot create a reservation inside block slot!'),
                        })

                        setTimeout(() => {
                            calendar?.removeEvent(createdEvent)
                        }, BLOCK_OVERLAP_SECONDS * 1000)
                    } else if (
                        props.availabilityDisplayColors
                        && checkBlockOverlapping(event, props.availabilityDisplayColors)
                    ) {
                        // Only the mirrored parent block overlaps -> table inside a blocked restaurant
                        setPendingBlockedChildCreate(event)
                    } else {
                        props.createNewReservation(event)
                    }
                }}
                connections={connections}
                onPageChange={handlePageChange}
                selectedDate={props.startDateFinal || props.fromDate}
                ref={props.calendarRef}
                firstDay={1}
                modules={myModules}
                colors={props.availabilityDisplayColors ?? props.availabilityColors}
                showEventBuffer={true}
            />

            <CommonConfirmDialog
                open={!!pendingBlockedChildCreate}
                onClose={cancelBlockedChildCreate}
                onConfirm={confirmBlockedChildCreate}
                title={t('Restaurant is blocked')}
                message={t('The restaurant is blocked during this time. Do you still want to create a reservation on this table?')}
                confirmLabel={t('Continue anyway')}
            />

            {(props.mbscResourceColumnType === "resources" || !props.mbscResourceColumnType) &&
                <CalendarEventReservationsTooltip
                    isTooltipOpen={!!hoveredEvent}
                    anchor={tooltipAnchor}
                    handleTooltipClose={closeTooltip}
                    event={hoveredEvent}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    contentRef={reservationTooltipContentRef}
                    reservations={props.reservations}
                    updateReservationInCache={props.updateReservationInCache}
                    onOpenReservation={(reservation) => {
                        closeTooltip()
                        handleEventOpen({reservationId: reservation.id})
                    }}
                />}

            {props.mbscResourceColumnType === "services" &&
                <CalendarEventServicesTooltip
                    isTooltipOpen={!!hoveredEvent}
                    anchor={tooltipAnchor as HTMLElement}
                    handleTooltipClose={() => setHoveredEvent(undefined)}
                    event={hoveredEvent?.event}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    services={props.services}
                    ageCategories={props.ageCategories}
                />}

        </Box>
    )
}

export default CalendarComponent
